import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./DevNavigation.module.css";
import Board from "../Board";
import PieceDrag from "../PieceDrag";
import { GameContext } from "../Providers/GameContext";
import { AuthContext } from "../Providers/AuthContext";
import ZodiacEngineService from "../ZodiacEngineService";
import { Position, Mode, PieceType, State } from "../../services/zodiacEngine/types";
import { Piece } from "../../services/zodiacEngine/Piece";
import { FENLoader } from "../FENLoader/FENLoader";

interface NavLink {
  label: string;
  path: string;
}

const mainPages: NavLink[] = [
  { label: "Home", path: "/main" },
  { label: "Challenge AI", path: "/challenge-ai" },
  { label: "Multiplayer", path: "/multiplayer" },
  { label: "Tutorials", path: "/tutorials" },
  { label: "Piece Rules", path: "/piecerules" },
  { label: "Sandbox", path: "/sandbox" },
  { label: "What's My Zodiac", path: "/whats-my-zodiac" },
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Terms of Service", path: "/terms" },
  { label: "Cookie Policy", path: "/cookies" },
];

const developmentPages: NavLink[] = [
  { label: "Analytics", path: "/analytics" },
  { label: "History", path: "/history" },
  { label: "Leaderboard", path: "/leaderboard" },
  { label: "Courses", path: "/courses" },
  { label: "Shop", path: "/shop" },
  { label: "Collection", path: "/collection" },
];

const backendPages: NavLink[] = [
  { label: "Page Design", path: "/pagedesign" },
  { label: "Testing Page", path: "/testing" },
  { label: "Tutorial Dev", path: "/tutorial-dev" },
  { label: "AI Arena", path: "/ai-arena" },
  { label: "Quiz Flow Canvas", path: "/character-flow-canvas.html" },
  { label: "Puzzle Designer", path: "/puzzle-designer" },
  { label: "Bug Reports", path: "/bug-reports" },
];

function DevNavigation() {
  const navigate = useNavigate();
  const [gameId, setGameId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pasteGameId, setPasteGameId] = useState("");

  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    updateGameState,
    updateSelectedPiece,
    updatePotentialMoves,
    setIsWatchMode,
  } = useContext(GameContext)!;

  const { user } = useContext(AuthContext);

  // Initialize dev mode on mount
  useEffect(() => {
    setIsWatchMode(false);
    ZodiacEngineService.zodiacEngine.switchToDevMove();
    updateSelectedPiece(undefined);
    updatePotentialMoves([]);
    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(newGameState);
    setGameId(ZodiacEngineService.zodiacEngine.getGameId() || null);
  }, []);

  const copyGameIdToClipboard = async () => {
    if (!gameId) return;
    try {
      await navigator.clipboard.writeText(gameId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
    }
  };

  const handleLoadFEN = (fenString: string) => {
    const result = ZodiacEngineService.zodiacEngine.loadFromFEN(fenString);
    if (result.success) {
      const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(newGameState);
      setGameId(ZodiacEngineService.zodiacEngine.getGameId() || null);
    }
    return result;
  };

  const handleLoadGameById = () => {
    if (!pasteGameId.trim()) return;
    // Load the game by ID - this navigates to the game
    navigate(`/${pasteGameId.trim()}`);
  };

  const handleCreateGame = async () => {
    if (!user) return;
    const newGameId = await ZodiacEngineService.zodiacEngine.switchToLiveMove(user.uid);
    setGameId(newGameId);
    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(newGameState);
  };

  function handleDrop(
    event: React.DragEvent<HTMLDivElement>,
    position: Position
  ) {
    event.preventDefault();
    const pieceData = event.dataTransfer.getData("piece");

    if (!pieceData) return;
    const { type, isLight } = JSON.parse(pieceData);

    ZodiacEngineService.zodiacEngine.placePiece(
      new Piece(type as PieceType, isLight),
      position
    );
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }

  const renderButtons = (links: NavLink[], category: string) => (
    <div className={styles.buttonGrid}>
      {links.map((link) => {
        const isExternal = link.path.endsWith('.html');

        if (isExternal) {
          return (
            <a
              key={link.path}
              href={link.path}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.navButton} ${styles[category]}`}
            >
              <span className={styles.buttonLabel}>{link.label}</span>
            </a>
          );
        }

        return (
          <Link
            key={link.path}
            to={link.path}
            className={`${styles.navButton} ${styles[category]}`}
          >
            <span className={styles.buttonLabel}>{link.label}</span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <div className={styles.container}>
      {/* Left sidebar with navigation */}
      <div className={styles.sidebar}>
        <h1 className={styles.title}>Dev Navigation</h1>

        <div className={styles.categories}>
          <div className={styles.category}>
            <h2 className={`${styles.categoryTitle} ${styles.main}`}>Main</h2>
            {renderButtons(mainPages, "main")}
          </div>

          <div className={styles.category}>
            <h2 className={`${styles.categoryTitle} ${styles.development}`}>In Development</h2>
            {renderButtons(developmentPages, "development")}
          </div>

          <div className={styles.category}>
            <h2 className={`${styles.categoryTitle} ${styles.backend}`}>Backend</h2>
            {renderButtons(backendPages, "backend")}
          </div>
        </div>
      </div>

      {/* Right side with board */}
      <div className={styles.boardSection}>
        {/* Pieces on left */}
        <div className={styles.pieceSelection}>
          <PieceDrag isDevMode={true} isLight={true} />
        </div>

        {/* Board in center */}
        <div className={styles.boardWrapper}>
          <Board
            gameState={gameState}
            uiState={{
              potentialMoves: potentialMoves,
              potentialSideEffects: potentialSideEffects,
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          />
        </div>

        {/* Controls on right */}
        <div className={styles.controlsSection}>
          {/* Game Info Section */}
          <div className={styles.gameInfoBox}>
            <div className={styles.gameInfoRow}>
              <span className={styles.gameInfoLabel}>State:</span>
              <span className={styles.gameInfoValue}>{gameState.state}</span>
            </div>
            <div className={styles.gameInfoRow}>
              <span className={styles.gameInfoLabel}>Turn:</span>
              <span className={styles.gameInfoValue}>{gameState.isLightTurn ? "Light" : "Dark"}</span>
            </div>
            <div className={styles.gameInfoRow}>
              <span className={styles.gameInfoLabel}>Turn Count:</span>
              <span className={styles.gameInfoValue}>{gameState.turnCount}</span>
            </div>
            <div className={styles.gameInfoRow}>
              <span className={styles.gameInfoLabel}>Mode:</span>
              <span className={styles.gameInfoValue}>{gameState.mode}</span>
            </div>
          </div>

          {/* Create Game Button */}
          <button
            className={styles.createGameButton}
            onClick={handleCreateGame}
            disabled={!user}
          >
            Create Game
          </button>

          <div className={styles.gameIdBox}>
            <span className={styles.gameIdLabel}>Current Game ID</span>
            <span className={styles.gameIdValue}>
              {gameId ? gameId : "No Game ID"}
            </span>
            <button
              className={styles.copyButton}
              onClick={copyGameIdToClipboard}
            >
              {copied ? "Copied!" : "Copy Game ID"}
            </button>
          </div>

          <div className={styles.gameIdBox}>
            <span className={styles.gameIdLabel}>Load Game by ID</span>
            <input
              type="text"
              className={styles.pasteInput}
              placeholder="Paste game ID here..."
              value={pasteGameId}
              onChange={(e) => setPasteGameId(e.target.value)}
            />
            <button
              className={styles.pasteButton}
              onClick={handleLoadGameById}
            >
              Load Game
            </button>
          </div>

          <FENLoader
            darkMode={true}
            onLoadFEN={handleLoadFEN}
          />
        </div>
      </div>
    </div>
  );
}

export default DevNavigation;
